import React, { useState, useEffect, useCallback } from 'react';

import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PipelineColumn from './components/PipelineColumn';
import PipelineFilters from './components/PipelineFilters';
import AddDealModal from './components/AddDealModal';
import PipelineStats from './components/PipelineStats';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { createInAppNotification } from '../../utils/notifications';

const Pipeline = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAddDealModalOpen, setIsAddDealModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [deals, setDeals] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', owner: 'all', priority: 'all', dateRange: 'all', minValue: 0, maxValue: 0, startDate: '', endDate: '' });
  const { profile } = useAuth();

  const fetchStages = useCallback(async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase?.from('pipeline_stages')?.select('*')?.eq('org_id', profile?.org_id)?.order('stage_order');
    if (data) setPipelineStages(data?.map(s => ({ id: s?.id, name: s?.name, color: s?.color || 'blue', dbId: s?.id })));
  }, [profile?.org_id]);

  const fetchDeals = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('deals')?.select('*, accounts(name), pipeline_stages(id, name, color), owner:profiles(id, full_name)')?.eq('org_id', profile?.org_id)?.order('created_at', { ascending: false });

      if (error) throw error;

      const mapped = (data || [])?.map(d => ({
        id: d?.id,
        title: d?.name,
        accountName: d?.accounts?.name || '',
        value: Number(d?.value) || 0,
        owner: { id: d?.owner?.id || '', name: d?.owner?.full_name || 'Unassigned', avatar: null },
        closeDate: d?.close_date || '',
        priority: d?.priority ? d?.priority?.charAt(0)?.toUpperCase() + d?.priority?.slice(1) : 'Medium',
        probability: d?.probability || 50,
        stage: d?.stage_id || '',
        tags: [],
        createdAt: d?.created_at,
        updatedAt: d?.updated_at
      }));

      setDeals(mapped);
    } catch (err) {
      console.error('Pipeline fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  useEffect(() => {
    fetchStages();
    fetchDeals();
  }, [fetchStages, fetchDeals]);

  // Realtime subscription
  useEffect(() => {
    if (!profile?.org_id) return;
    const channel = supabase?.channel('pipeline_realtime')?.on('postgres_changes', { event: '*', schema: 'public', table: 'deals', filter: `org_id=eq.${profile?.org_id}` }, fetchDeals)?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [profile?.org_id, fetchDeals]);

  const handleSidebarToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const handleAddDeal = (stageId = null) => {
    setSelectedStage(stageId);
    setIsAddDealModalOpen(true);
  };

  const handleSaveDeal = async (newDeal) => {
    try {
      const stageId = selectedStage || (pipelineStages?.[0]?.dbId || pipelineStages?.[0]?.id);
      await supabase?.from('deals')?.insert({
        name: newDeal?.title || newDeal?.name,
        org_id: profile?.org_id,
        owner_id: profile?.id,
        value: Number(newDeal?.value) || 0,
        stage_id: stageId,
        priority: (newDeal?.priority || 'medium')?.toLowerCase(),
        probability: newDeal?.probability || 50,
        close_date: newDeal?.closeDate || null
      });
      // In-app notification for new deal
      await createInAppNotification({
        org_id: profile?.org_id,
        type: 'job_update',
        title: `New Deal: ${newDeal?.title || newDeal?.name}`,
        body: `Value: $${Number(newDeal?.value || 0)?.toLocaleString()}`,
        link: '/pipeline',
      });
      fetchDeals();
    } catch (err) {
      console.error('Save deal error:', err);
    }
  };

  const handleDealMove = async (dealId, newStageId) => {
    // Optimistic update
    setDeals(prev => prev?.map(d => d?.id === dealId ? { ...d, stage: newStageId } : d));
    try {
      await supabase?.from('deals')?.update({ stage_id: newStageId })?.eq('id', dealId);
    } catch (err) {
      console.error('Move deal error:', err);
      fetchDeals(); // Revert on error
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Delete this deal?')) return;
    try {
      await supabase?.from('deals')?.delete()?.eq('id', dealId);
      fetchDeals();
    } catch (err) {
      console.error('Delete deal error:', err);
    }
  };

  const handleCloneDeal = async (deal) => {
    try {
      await supabase?.from('deals')?.insert({
        name: `${deal?.title} (Copy)`,
        org_id: profile?.org_id,
        owner_id: profile?.id,
        value: deal?.value,
        stage_id: deal?.stage,
        priority: (deal?.priority || 'medium')?.toLowerCase(),
        probability: deal?.probability
      });
      fetchDeals();
    } catch (err) {
      console.error('Clone deal error:', err);
    }
  };

  const handleFiltersChange = (newFilters) => setFilters(newFilters);
  const handleResetFilters = () => setFilters({ search: '', owner: 'all', priority: 'all', dateRange: 'all', minValue: 0, maxValue: 0, startDate: '', endDate: '' });

  const filteredDeals = deals?.filter(deal => {
    if (filters?.search && !deal?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
    if (filters?.priority !== 'all' && deal?.priority?.toLowerCase() !== filters?.priority) return false;
    return true;
  });

  return (
    <>
      <Helmet>
        <title>Pipeline - Wilcart CRM</title>
        <meta name="description" content="Visual sales pipeline with drag-and-drop deal management in Wilcart CRM." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleSidebarToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Pipeline</h1>
                <p className="text-muted-foreground mt-1">Visualize and manage your sales pipeline</p>
              </div>
              <Button onClick={() => handleAddDeal(null)}>
                <Icon name="Plus" size={16} className="mr-2" />
                Add Deal
              </Button>
            </div>

            <PipelineStats deals={filteredDeals} stages={pipelineStages} />

            <PipelineFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onResetFilters={handleResetFilters}
            />

            {loading ? (
              <div className="flex space-x-4 overflow-x-auto pb-4">
                {[1,2,3,4]?.map(i => (
                  <div key={i} className="flex-shrink-0 w-72">
                    <div className="h-8 bg-muted animate-pulse rounded mb-3" />
                    {[1,2]?.map(j => <div key={j} className="h-32 bg-muted animate-pulse rounded-lg mb-3" />)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex space-x-4 overflow-x-auto pb-4">
                {pipelineStages?.map((stage) => (
                  <PipelineColumn
                    key={stage?.id}
                    stage={stage}
                    deals={filteredDeals?.filter(d => d?.stage === stage?.id)}
                    onAddDeal={() => handleAddDeal(stage?.id)}
                    onDealMove={handleDealMove}
                    onEditDeal={() => {}}
                    onDeleteDeal={handleDeleteDeal}
                    onCloneDeal={handleCloneDeal}
                  />
                ))}
              </div>
            )}
          </div>
        </main>

        <AddDealModal
          isOpen={isAddDealModalOpen}
          onClose={() => setIsAddDealModalOpen(false)}
          onSave={handleSaveDeal}
          selectedStage={selectedStage}
          stages={pipelineStages}
        />
      </div>
    </>
  );
};

export default Pipeline;